/* @flow */

import { omit, chain } from 'lodash'
import { createSelector } from 'reselect'

import type {
  RootState,
  Poll,
  Action,
  PollsStore,
  PollsQuery,
  PollId,
  Vector,
  CheckedoutPoll,
  PollTypeId,
  CustomerId,
} from '../types'
import {
  parseIntOr,
  upsert,
  ActionRequiresPropertyError,
  normalizeVector,
  vectorLength,
} from '../helpers'

import {
  isPollTypeId1D,
  isPollTypeId2D,
  isPollTypeIdRanking,
  isPollTypeIdSlider,
} from './pollTypesStore'

import { getRoot } from './rootSelector'

export const CLEAR_POLLS_STORE: string = 'opinary/CLEAR_POLLS_STORE'
export const INTO_POLLS_STORE: string = 'opinary/INTO_POLLS_STORE'
export const DELETE_FROM_POLLS_STORE_WHERE_ID: string =
  'opinary/DELETE_FROM_POLLS_STORE_WHERE_ID'

export const SET_POLL_FEATURED_FLAG: string = 'opinary/SET_POLL_FEATURED_FLAG'
export const SET_POLL_FEATURED_FLAG_SUCCESS: string =
  'opinary/SET_POLL_FEATURED_FLAG_SUCCESS'
export const SET_POLL_FEATURED_FLAG_ERROR: string =
  'opinary/SET_POLL_FEATURED_FLAG_ERROR'

export const SET_POLL_AUTOMATED_FLAG_ERROR: string =
  'opinary/SET_POLL_AUTOMATED_FLAG_ERROR'
export const SET_POLL_AUTOMATED_FLAG_SUCCESS: string =
  'opinary/SET_POLL_AUTOMATED_FLAG_SUCCESS'
export const SET_POLL_AUTOMATED_DISABLED_FLAG: string =
  'opinary/SET_POLL_AUTOMATED_DISABLED_FLAG'
// Action creators

export function clearPollsStore(): Action {
  return {
    type: CLEAR_POLLS_STORE,
  }
}

export function intoPollsStore(items: Array<Poll>): Action {
  return {
    type: INTO_POLLS_STORE,
    items,
  }
}

export function deleteFromPollsStoreWhereId(id: PollId): Action {
  return {
    type: DELETE_FROM_POLLS_STORE_WHERE_ID,
    id,
  }
}

export function setPollFeaturedFlag(pollId: PollId, featured: boolean): Action {
  return {
    type: SET_POLL_FEATURED_FLAG,
    pollId,
    featured,
  }
}

export function setPollFeaturedFlagSuccess(
  pollId: PollId,
  featured: boolean
): Action {
  return {
    type: SET_POLL_FEATURED_FLAG_SUCCESS,
    pollId,
    featured,
  }
}

export function setPollFeaturedFlagError(
  pollId: PollId,
  featured: boolean
): Action {
  return {
    type: SET_POLL_FEATURED_FLAG_ERROR,
    pollId,
    featured,
  }
}

// AUTOMATION 🤖

export const setPollAutomatedDisabledFlag = (
  pollId: PollId,
  automationDisabled: boolean
) => ({
  type: SET_POLL_AUTOMATED_DISABLED_FLAG,
  pollId,
  automationDisabled,
})

export const setPollAutomatedFlagSuccess = (
  pollId: PollId,
  automationDisabled: boolean
) => ({
  type: SET_POLL_AUTOMATED_FLAG_SUCCESS,
  pollId,
  automationDisabled,
})

export const setPollAutomatedFlagError = (
  pollId: PollId,
  automationDisabled: boolean
) => ({
  type: SET_POLL_AUTOMATED_FLAG_ERROR,
  pollId,
  automationDisabled,
})

// Selectors
export function selectAllPolls(rootState: RootState): Array<Poll> {
  return chain(rootState.pollsStore)
    .values()
    .map(item => item)
    .value()
}

export function selectPollWhereId(rootState: RootState, id: number): ?Poll {
  const item = rootState.pollsStore[id.toString()]
  if (item == null) {
    return null
  }

  return item
}

export const getPoll = createSelector(
  getRoot,
  ({ pollBuilderCheckedoutPoll }) => pollBuilderCheckedoutPoll
)

export const getSlider = createSelector(
  getPoll,
  ({ sliderMin, sliderMax, sliderSteps }) => ({
    sliderMin,
    sliderMax,
    sliderSteps,
  })
)

export default function reducer(
  state: PollsStore = {},
  action: Action
): PollsStore {
  let pollId
  let featured
  let automationDisabled
  let poll
  switch (action.type) {
    case INTO_POLLS_STORE:
      if (!action.items) {
        throw new ActionRequiresPropertyError(INTO_POLLS_STORE, 'items')
      } else {
        return upsert(state, action.items)
      }
    case CLEAR_POLLS_STORE:
      return {}
    case DELETE_FROM_POLLS_STORE_WHERE_ID:
      if (!action.id) {
        throw new ActionRequiresPropertyError(
          DELETE_FROM_POLLS_STORE_WHERE_ID,
          'id'
        )
      } else {
        return omit(state, action.id)
      }
    case SET_POLL_FEATURED_FLAG_SUCCESS:
      if (!action.pollId) {
        throw new ActionRequiresPropertyError(
          SET_POLL_FEATURED_FLAG_SUCCESS,
          'pollId'
        )
      }

      if (action.featured == null) {
        throw new ActionRequiresPropertyError(
          SET_POLL_FEATURED_FLAG_SUCCESS,
          'featured'
        )
      }
      pollId = action.pollId
      featured = action.featured
      poll = state[pollId]
      return {
        ...state,
        [pollId]: {
          ...poll,
          featured,
        },
      }

    case SET_POLL_AUTOMATED_FLAG_SUCCESS:
      if (!action.pollId) {
        throw new ActionRequiresPropertyError(
          SET_POLL_AUTOMATED_FLAG_SUCCESS,
          'pollId'
        )
      }

      if (action.automationDisabled == null) {
        throw new ActionRequiresPropertyError(
          SET_POLL_AUTOMATED_FLAG_SUCCESS,
          'automationDisabled'
        )
      }
      pollId = action.pollId
      automationDisabled = action.automationDisabled
      poll = state[pollId]
      return {
        ...state,
        [pollId]: {
          ...poll,
          automationDisabled,
        },
      }
    default:
      return state
  }
}

export function isPoll1D(poll: Poll | CheckedoutPoll): boolean {
  return isPollTypeId1D(poll.kind)
}

export function isPoll2D(poll: Poll | CheckedoutPoll): boolean {
  return isPollTypeId2D(poll.kind)
}

export function isPollRanking(poll: Poll | CheckedoutPoll): boolean {
  return isPollTypeIdRanking(poll.kind)
}

export function isPollSlider(poll: Poll | CheckedoutPoll): boolean {
  return isPollTypeIdSlider(poll.kind)
}

const sortByAllowed = ['name', 'views', 'created_at']

const sortByDefault = 'created_at'

const sortOrderAllowed = ['asc', 'desc']

const sortOrderDefault = 'desc'

export function parsePollsQuery(query: Object): PollsQuery {
  const result = {}

  if (query.search != null && query.search.trim() !== '') {
    result.search = query.search
  }

  if (query.onlyCustomerId != null && query.onlyCustomerId !== '') {
    result.onlyCustomerId = query.onlyCustomerId
  }

  result.page = parseIntOr(query.page, 0)
  result.perPage = parseIntOr(query.per_page, 50)

  if (sortByAllowed.includes(query.sort_by)) {
    result.sortBy = query.sort_by
  } else {
    result.sortBy = sortByDefault
  }

  if (sortOrderAllowed.includes(query.sort_order)) {
    result.sortOrder = query.sort_order
  } else {
    result.sortOrder = sortOrderDefault
  }

  return result
}

/*
 * returns the position closest to `position` where an expert opinion for
 * the given `poll` can be placed
 */
export function closestValidExpertOpinionPosition(
  poll: Poll | CheckedoutPoll,
  position: Vector
): Vector {
  let result = Object.assign({}, position)

  if (isPoll1D(poll)) {
    if (result.y > 0) {
      result.y = 0
    }
    result = normalizeVector(result)
  } else if (isPoll2D(poll)) {
    const length = vectorLength(result)
    if (length > 1) {
      result = normalizeVector(result)
    }
  }
  return result
}

const newPollBase = {
  id: null,
  featured: false,
  sponsored: false,
  automated: false,
  adminOnlyEdit: false,
  horizontal: false,
  designId: null,
  name: '',
  expertOpinionIds: [],
  tags: [],
  labelLeft: '',
  labelRight: '',
  header: '',
  labelTop: '',
  labelBottom: '',
  sliderUnit: '',
  sliderStepSize: '',
  sliderSteps: '11',
  sliderMin: '0',
  sliderMax: '10',
}

export function generateEmptyRankingOption(position: number) {
  // option ID must be unique within the poll. Randomizing
  // between 0 and 99999 should be enough.
  const optionId = Math.floor(Math.random() * 100000).toString()
  return {
    image: '',
    imageCredit: null,
    name: '',
    optionId: optionId,
    position,
  }
}

export function getFreshPoll(
  pollTypeId: PollTypeId,
  customerId: ?CustomerId
): CheckedoutPoll {
  switch (pollTypeId) {
    case 'ranking': {
      return {
        ...newPollBase,
        kind: pollTypeId,
        customer: customerId,
        options: [
          generateEmptyRankingOption(0),
          generateEmptyRankingOption(1),
          generateEmptyRankingOption(2),
        ],
      }
    }
    default: {
      return {
        ...newPollBase,
        kind: pollTypeId,
        customer: customerId,
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/pollsStore.js